import * as React from 'react';
import {ErrorBoundary} from '@sail/observability';
import {Toast} from './Toast';
import {ToastLayer} from './ToastLayer';
import type {ToastActionProps, ToastProps} from './types';

// Generate unique IDs for toasts
let toastIdCounter = 0;

const TOAST_ID_PREFIX = 'toast-provider-id-';

export const resetToastIdCounter = () => {
  toastIdCounter = 0;
};

const generateToastId = (userProvidedId?: string) => {
  if (userProvidedId) {
    if (userProvidedId.startsWith(TOAST_ID_PREFIX)) {
      const num = Number(userProvidedId.slice(TOAST_ID_PREFIX.length));
      if (Number.isInteger(num) && num >= toastIdCounter) {
        toastIdCounter = num + 1;
      }
    }
    return userProvidedId;
  }

  return `${TOAST_ID_PREFIX}${toastIdCounter++}`;
};

const TOAST_REMOVE_AFTER_EXIT_MS = 200;

// Dismiss toast error boundary
const SilentErrorView = () => null;

type ToastBaseOptions = {
  /**
   * The message to display in the toast
   */
  message: React.ReactNode;
  /**
   * The type of toast
   */
  type: ToastProps['type'];
  /**
   * The duration of the toast before auto-dismissing
   */
  duration?: ToastProps['duration'];
  /**
   * Controls whether the toast should automatically dismiss
   */
  autoDismiss?: ToastProps['autoDismiss'];
  /**
   * Where to place the toast (top or bottom)
   */
  placement?: ToastProps['placement'];
  /**
   * Pending toast progress (0–100). See `ToastProps['progress']`.
   */
  progress?: ToastProps['progress'];
  /**
   * Callback to be called when the toast action is clicked
   */
  onDismiss?: () => void;
};

export type ToastOptions = ToastBaseOptions &
  ToastActionProps & {
    /**
     * Toast ID to use. When provided, if there is a toast being displayed with the same ID,
     * it will be updated instead of a new one being shown.
     */
    id?: string;
  };

type ToastItem = ToastOptions & {
  id: string;
  isOpen: boolean;
  revision: number;
};

type ToastContextType = {
  /**
   * Show a toast and return its ID for later dismissal
   */
  show: (options: ToastOptions) => string;
  /**
   * Update an existing toast by ID
   */
  update: (
    id: string,
    options: Partial<ToastBaseOptions> & ToastActionProps,
  ) => void;
  /**
   * Dismiss a specific toast by ID
   */
  dismiss: (id: string) => void;
  /**
   * Dismiss all active toasts
   */
  dismissAll: () => void;
};

const ToastContext = React.createContext<ToastContextType | null>(null);

export const ToastProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [toasts, setToasts] = React.useState<ToastItem[]>([]);

  const removalTimers = React.useRef<
    Map<string, ReturnType<typeof setTimeout>>
  >(new Map());
  const cancelRemoval = React.useCallback((id: string) => {
    const timer = removalTimers.current.get(id);
    if (timer !== undefined) {
      clearTimeout(timer);
      removalTimers.current.delete(id);
    }
  }, []);

  const show = React.useCallback<ToastContextType['show']>(
    ({placement = 'bottom', id: providedId, ...options}) => {
      const id = generateToastId(providedId);

      setToasts((prev) => {
        const hasExistingToast =
          providedId && prev.some((toast) => toast.id === providedId);
        if (hasExistingToast) {
          // Cancel pending removal timeout, if it exists
          cancelRemoval(providedId);
          return prev.map((toast) =>
            toast.id === providedId
              ? {
                  ...toast,
                  ...options,
                  placement,
                  isOpen: true,
                  // Bump revision to reset the auto-dismiss timer
                  revision: toast.revision + 1,
                }
              : toast,
          );
        }
        return [
          ...prev,
          {...options, id, isOpen: false, placement, revision: 0},
        ];
      });

      /**
       * TODO: use startTransition when we migrate to React 19
       * We need firstly to render ToastLayer and then update children
       * so appearing animation is smooth and we do not affect render count
       * for RootLayer
       */
      requestAnimationFrame(() => {
        setToasts((prev) =>
          prev.map((toast) =>
            toast.id === id ? {...toast, isOpen: true} : toast,
          ),
        );
      });

      return id;
    },
    [cancelRemoval],
  );

  const update = React.useCallback<ToastContextType['update']>(
    (id, options) => {
      // Cancel pending removal timeout, if it exists
      cancelRemoval(id);
      setToasts((prev) =>
        prev.map((toast) =>
          toast.id === id
            ? {
                ...toast,
                ...options,
                isOpen: true,
                // Bump revision to reset the auto-dismiss timer
                revision: toast.revision + 1,
              }
            : toast,
        ),
      );
    },
    [cancelRemoval],
  );

  const makeRemovalTimer = React.useCallback(
    (id: string) => {
      // Cancel an already scheduled removal timeout, if it exists
      cancelRemoval(id);

      const timer = setTimeout(() => {
        // Remove self from the removal timer map and the toasts list
        removalTimers.current.delete(id);
        setToasts((v) => v.filter((toast) => toast.id !== id));
      }, TOAST_REMOVE_AFTER_EXIT_MS);

      // Record the new timer in the removal timer map
      removalTimers.current.set(id, timer);
    },
    [cancelRemoval],
  );

  const dismiss = React.useCallback<ToastContextType['dismiss']>(
    (id) => {
      setToasts((prev) => {
        const newToasts = prev.map((toast) =>
          toast.id === id ? {...toast, isOpen: false} : toast,
        );

        makeRemovalTimer(id);

        return newToasts;
      });
    },
    [makeRemovalTimer],
  );

  const dismissAll = React.useCallback(() => {
    // Note: we can't just use `dismiss` here because all updates to
    // the `toasts` array need to be done in one `setToasts` call since
    // we're modifying the entire array at once
    setToasts((prev) => {
      // Kick off a removal timer for each toast
      for (const toast of prev) {
        makeRemovalTimer(toast.id);
      }

      // Mark all toasts as closed
      return prev.map((toast) => ({...toast, isOpen: false}));
    });
  }, [makeRemovalTimer]);

  const handleToastDismiss = React.useCallback(
    (toast: ToastItem) => () => {
      dismiss(toast.id);
      toast.onDismiss?.();
    },
    [dismiss],
  );

  const value = React.useMemo(
    () => ({show, update, dismiss, dismissAll}),
    [show, update, dismiss, dismissAll],
  );

  const topToasts = React.useMemo(
    () => toasts.filter((t) => t.placement === 'top'),
    [toasts],
  );
  const bottomToasts = React.useMemo(
    () => toasts.filter((t) => t.placement === 'bottom'),
    [toasts],
  );

  const hasTopLayer = topToasts.length > 0;
  const hasBottomLayer = bottomToasts.length > 0;

  return (
    <ToastContext.Provider value={value}>
      {children}

      {hasTopLayer ? (
        <ToastLayer placement="top">
          {topToasts.map((toast) => (
            <ErrorBoundary
              uuid="C1E2FS00"
              key={toast.id}
              errorView={SilentErrorView}
              component="Toast"
            >
              <Toast {...toast} onDismiss={handleToastDismiss(toast)}>
                {toast.message}
              </Toast>
            </ErrorBoundary>
          ))}
        </ToastLayer>
      ) : null}

      {hasBottomLayer ? (
        <ToastLayer placement="bottom">
          {bottomToasts.map((toast) => (
            <ErrorBoundary
              uuid="C1E2FS01"
              key={toast.id}
              errorView={SilentErrorView}
              component="Toast"
            >
              <Toast {...toast} onDismiss={handleToastDismiss(toast)}>
                {toast.message}
              </Toast>
            </ErrorBoundary>
          ))}
        </ToastLayer>
      ) : null}
    </ToastContext.Provider>
  );
};

/**
 * Hook to show, dismiss, and manage toasts.
 *
 * Must be used within a ToastProvider.
 *
 * @example
 * ```tsx
 * const toast = useToast();
 *
 * // Show a toast and get its ID
 * const id = toast.show({
 *   message: 'Operation successful!',
 *   type: 'neutral',
 * });
 *
 * // Update an existing toast
 * toast.update(id, {
 *   message: 'Updated message',
 *   type: 'error',
 * });
 *
 * // Dismiss a specific toast
 * toast.dismiss(id);
 *
 * // Dismiss all toasts
 * toast.dismissAll();
 * ```
 */
export const useToast = (): ToastContextType => {
  const context = React.useContext(ToastContext);
  if (!context) {
    throw new Error(
      'useToast must be used within a ToastProvider. Please ensure you have wrapped your application in a <RootLayer /> component.',
    );
  }
  return context;
};
